/**
 * Instagram oEmbed Block
 *
 * Renders an Instagram profile embed using the property's
 * `property_instagram_url` field. Extracts the account handle from the URL
 * and constructs the standard Instagram blockquote markup, then loads
 * Instagram's embed.js to hydrate it into the rich profile preview.
 */

import { useEffect } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';
import { useExternalScript } from '../../hooks/useExternalScript';

const EMBED_SCRIPT_SRC = 'https://www.instagram.com/embed.js';

const NON_HANDLE_PREFIXES = new Set([
  'p', 'reel', 'reels', 'tv', 'explore', 'accounts', 'direct', 'about',
]);

function sanitizeHandle(raw: string | undefined | null): string | null {
  if (!raw) return null;
  const cleaned = raw.trim().replace(/^@+/, '').split(/[/?#\s]/)[0];
  if (!cleaned) return null;
  // Instagram handles are alphanumeric, underscores, and periods.
  return /^[A-Za-z0-9._]+$/.test(cleaned) ? cleaned : null;
}

function extractHandle(input: string | null | undefined): string | null {
  if (!input) return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('@')) {
    return sanitizeHandle(trimmed.slice(1));
  }

  try {
    const url = new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`);
    const segments = url.pathname.split('/').filter(Boolean);
    if (segments.length === 0) return null;
    // /stories/handle/... → take the second segment.
    if (segments[0] === 'stories' && segments[1]) {
      return sanitizeHandle(segments[1]);
    }
    if (NON_HANDLE_PREFIXES.has(segments[0].toLowerCase())) return null;
    return sanitizeHandle(segments[0]);
  } catch {
    const fallback = trimmed.split(/[/?#]/).filter(Boolean).pop();
    return sanitizeHandle(fallback);
  }
}

function InstagramOembedComponent({ sectionId: _sectionId }: BlockProps) {
  const instagramUrl = useBlockToken('instagram_url', {
    defaultField: 'property_instagram_url',
  });
  const handle = extractHandle(instagramUrl);

  const status = useExternalScript(handle ? EMBED_SCRIPT_SRC : null);

  useEffect(() => {
    if (!handle || status !== 'ready') return;
    const w = window as unknown as { instgrm?: { Embed?: { process?: () => void } } };
    w.instgrm?.Embed?.process?.();
  }, [handle, status]);

  if (!handle) {
    return (
      <p style={{ color: 'var(--vibe-color-text-muted)', margin: 0, textAlign: 'center' }}>
        Add an Instagram URL to display the embed.
      </p>
    );
  }

  const profileUrl = `https://www.instagram.com/${handle}/?utm_source=ig_embed&utm_campaign=loading`;

  return (
    <div className="flex w-full justify-center">
      <blockquote
        key={handle}
        className="instagram-media"
        data-instgrm-permalink={profileUrl}
        data-instgrm-version="14"
        style={{
          background: '#FFF',
          border: 0,
          borderRadius: '3px',
          boxShadow: '0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)',
          margin: '1px',
          maxWidth: '540px',
          minWidth: '326px',
          padding: 0,
          width: 'calc(100% - 2px)',
        }}
      >
        <div style={{ padding: '16px' }}>
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              lineHeight: 0,
              padding: 0,
              textAlign: 'center',
              textDecoration: 'none',
              width: '100%',
            }}
          >
            <div style={{ padding: '24% 0' }} />
            <p
              style={{
                color: '#c9c8cd',
                fontFamily: 'Arial,sans-serif',
                fontSize: '14px',
                lineHeight: '17px',
                marginBottom: 0,
                marginTop: '8px',
                overflow: 'hidden',
                padding: '8px 0 7px',
                textAlign: 'center',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              View @{handle} on Instagram
            </p>
          </a>
        </div>
      </blockquote>
    </div>
  );
}

const contentSchema: ComponentContentSchema = {
  slot: 'InstagramOembed',
  displayName: 'Instagram Embed',
  tokens: {
    instagram_url: {
      label: 'Instagram URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      description:
        'Full Instagram profile URL or handle. Defaults to the property\'s Instagram URL.',
      suggestedFields: ['property_instagram_url'],
      required: true,
    },
  },
};

export const instagramOembedBlock: BlockDefinition = {
  id: 'instagram-oembed',
  name: 'Instagram Embed',
  description:
    'Embeds an Instagram profile preview using the property\'s Instagram URL.',
  category: 'Social',
  component: InstagramOembedComponent,
  contentSchema,
  requirements: {
    designTokens: [],
    dataTokens: [],
  },
  tags: ['instagram', 'embed', 'social', 'oembed', 'profile'],
  defaultGridSpan: 12,
};
